/**
 * "Where did this work item stop, and why?"
 *
 * The obligation chain view. Three refusals are deliberate here, because each
 * one is a way this screen could quietly lie:
 *
 * 1. **A receipt is never rendered as a tick.** `aggregateStatus` is shown as
 *    itself — PASS, FAIL, NOT_RUN and UNKNOWN each get their own badge AND
 *    their own sentence — and `assurance` is shown beside it as a separate
 *    fact. A receipt can be signed, contract-bound and digest-bound while its
 *    verdict is NOT_RUN; 81 receipts in this vault are readable and 1 reads
 *    exactly that way. `NOT_RUN` is nearer to nothing-happened than to a pass,
 *    so it is coloured as a refusal and captioned as one.
 * 2. **A fixture is never indistinguishable from real work.** The subject class
 *    is derived server-side from the Work's own `source.uri`, and this view
 *    always prints the uri and the rule that classified it next to the badge.
 * 3. **Null is never an empty cell.** A missing predecessor renders the words
 *    "No predecessor recorded", so absence cannot be misread as a value that
 *    failed to load.
 */

import { formatDateTime } from "@excellent/ui";
import type {
  VerificationWorkChain,
  VerificationWorkChainObligation,
  VerificationWorkChainReceipt,
} from "@excellent/ui/api/contracts/verification-work-chain";

import {
  Callout,
  Card,
  CardContent,
  DataTable,
  Field,
  Fields,
  Mono,
  Section,
  Stat,
  StatGrid,
  ToneBadge,
  Verdict,
  type DataTableColumn,
  type Tone,
} from "../../ui";

const NO_PREDECESSOR = "No predecessor recorded";

const STOP_STAGE_LABEL: Record<VerificationWorkChain["stop"]["stage"], string> = {
  contract: "Contract",
  attempt: "Attempt",
  admission: "Obligation admission",
  determination: "Obligation determination",
  replay: "Independent replay",
  verdict: "Aggregate verdict",
  complete: "Nothing refused",
};

const OUTCOME_TONE: Record<VerificationWorkChainObligation["outcome"], Tone> = {
  PASS: "pass",
  MISMATCH: "fail",
  FAIL: "fail",
  UNKNOWN: "unknown",
  NOT_RUN: "neutral",
  PENDING: "running",
  REJECTED: "fail",
  NOT_DETERMINED: "neutral",
};

/**
 * One sentence per aggregate status. NOT_RUN and UNKNOWN get explicit "is not a
 * pass" copy: the whole failure mode this screen exists to prevent is a reader
 * treating a present, valid, signed receipt as evidence that something worked.
 */
const VERDICT_COPY: Record<VerificationWorkChainReceipt["aggregateStatus"], string> = {
  PASS: "Every obligation was evaluated and the signed aggregate verdict is PASS.",
  MISMATCH:
    "The signed aggregate verdict is MISMATCH — an independent record was consulted and CONTRADICTED the claim. This is stronger than FAIL: it is not that the work could not be confirmed, it is that the evidence shows it was done wrong.",
  FAIL: "The signed aggregate verdict is FAIL — at least one declared obligation was evaluated and did not hold.",
  NOT_RUN:
    "NOT_RUN is not a pass. This receipt is signed and bound to a real contract and digest, but the verification did not run, so nothing about this work was established.",
  UNKNOWN:
    "UNKNOWN is not a pass. The verifier reached no determination, so this receipt establishes nothing about whether the work holds.",
  PENDING:
    "PENDING is not a pass. The verification is inside a stated window and is not yet due; this receipt carries the deadline it resolves by, and at that deadline it becomes a definite verdict that is never PASS.",
};

/**
 * Four distinct colours for four distinct states: PASS green, FAIL red,
 * NOT_RUN/UNKNOWN amber, PENDING in-flight. NOT_RUN deliberately does NOT share
 * FAIL's red (it is not a failed check) and deliberately does NOT share PASS's
 * green or the muted default (either would let "nothing ran" read as "nothing
 * wrong"). The union was widened rather than PENDING collapsed into `unknown`,
 * because sharing amber is precisely how a reader would be told to act on a
 * check that is merely not yet due.
 */
const VERDICT_STAT_TONE: Record<
  VerificationWorkChainReceipt["aggregateStatus"],
  "pass" | "fail" | "unknown" | "accent"
> = {
  PASS: "pass",
  MISMATCH: "fail",
  FAIL: "fail",
  UNKNOWN: "unknown",
  NOT_RUN: "unknown",
  // Not amber. Amber on this screen means "nothing was established and that is a
  // problem"; a verdict still inside its window is not a problem yet, and
  // colouring it as one is what would make a reader act on a timer. `accent` is
  // the fourth tone this surface's StatCard accepts — `running` is in the wider
  // `Tone` union but not in this one, so the distinct colour is taken from the
  // vocabulary the component actually has rather than added to it.
  PENDING: "accent",
};

const ASSURANCE_COPY: Record<VerificationWorkChainReceipt["assurance"], string> = {
  PROSPECTIVE:
    "Prospective — the contract was accepted before the work was judged, so this record can carry completion authority.",
  RETROSPECTIVE:
    "Retrospective — this record was written about work that had already happened, so it cannot authorise completion.",
};

function stopTone(stage: VerificationWorkChain["stop"]["stage"]): "pass" | "unknown" | "fail" {
  return stage === "complete" ? "pass" : stage === "verdict" ? "unknown" : "fail";
}

function shortDigest(value: string): string {
  return value.length > 34 ? `${value.slice(0, 18)}…${value.slice(-10)}` : value;
}

function ReasonCodes({ codes }: { codes: readonly string[] }) {
  if (codes.length === 0) {
    return <span className="type-subtext text-muted-foreground">No reason code recorded</span>;
  }
  return (
    <span className="flex flex-wrap gap-1.5">
      {codes.map((code) => (
        <ToneBadge key={code} tone="neutral">
          <Mono>{code}</Mono>
        </ToneBadge>
      ))}
    </span>
  );
}

function obligationColumns(
  chain: VerificationWorkChain,
): DataTableColumn<VerificationWorkChainObligation>[] {
  return [
    {
      key: "obligation",
      header: "Obligation",
      cellClassName: "whitespace-normal",
      render: (obligation) => (
        <div className="flex flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-1.5">
            <strong>{obligation.role}</strong>
            {chain.stop.obligationId === obligation.obligationId ? (
              <ToneBadge tone={stopTone(chain.stop.stage)}>Stopped here</ToneBadge>
            ) : null}
            {obligation.required ? null : <ToneBadge tone="neutral">Optional</ToneBadge>}
          </span>
          <span className="type-subtext text-muted-foreground">
            <Mono>{obligation.obligationId}</Mono>
          </span>
        </div>
      ),
    },
    {
      key: "classification",
      header: "Class",
      render: (obligation) => obligation.classification ?? "Not classified",
    },
    {
      key: "predicate",
      header: "Predicate",
      render: (obligation) => <Mono>{obligation.predicateKind}</Mono>,
    },
    {
      key: "admission",
      header: "Admission",
      render: (obligation) =>
        obligation.admission ? (
          <ToneBadge tone={obligation.admission.decision === "admitted" ? "pass" : "fail"}>
            {obligation.admission.decision}
          </ToneBadge>
        ) : (
          <span className="type-subtext text-muted-foreground">No admission record</span>
        ),
    },
    {
      key: "outcome",
      header: "Determination",
      render: (obligation) =>
        obligation.determination ? (
          <Verdict status={obligation.determination.status} />
        ) : (
          <ToneBadge tone={OUTCOME_TONE[obligation.outcome]}>
            {obligation.outcome === "REJECTED" ? "Never evaluated" : "No determination"}
          </ToneBadge>
        ),
    },
    {
      // Chain-only Work ids never reach the Evidence tab; render the count the
      // contract already emits or Evidence is invisible on that path.
      key: "evidence",
      header: "Evidence",
      render: (obligation) =>
        obligation.determination ? (
          <span data-work-chain-evidence={obligation.determination.evidenceArtifactCount}>
            {obligation.determination.evidenceArtifactCount} artifact
            {obligation.determination.evidenceArtifactCount === 1 ? "" : "s"}
          </span>
        ) : (
          <span className="type-subtext text-muted-foreground" data-work-chain-evidence="none">
            No evidence recorded
          </span>
        ),
    },
    {
      key: "reasons",
      header: "Reason codes",
      cellClassName: "whitespace-normal",
      render: (obligation) => (
        <ReasonCodes
          codes={
            obligation.determination?.reasonCodes ??
            (obligation.admission?.decision === "rejected" ? obligation.admission.reasonCodes : [])
          }
        />
      ),
    },
    {
      key: "rationale",
      header: "What the evaluator said",
      cellClassName: "whitespace-normal",
      render: (obligation) =>
        obligation.determination ? (
          <span className="type-subtext">{obligation.determination.rationale}</span>
        ) : (
          <span className="type-subtext text-muted-foreground">Nothing was evaluated.</span>
        ),
    },
  ];
}

function ReceiptCard({
  receipt,
  latest,
}: {
  receipt: VerificationWorkChainReceipt;
  latest: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Verdict status={receipt.aggregateStatus} size={latest ? "lg" : undefined} />
          <ToneBadge tone={receipt.assurance === "PROSPECTIVE" ? "accent" : "unknown"}>
            {receipt.assurance}
          </ToneBadge>
          <ToneBadge tone={latest ? "accent" : "neutral"}>
            {latest ? "Latest receipt" : "Earlier receipt"}
          </ToneBadge>
        </div>
        <p className="type-body" data-work-chain-verdict-copy={receipt.aggregateStatus}>
          {VERDICT_COPY[receipt.aggregateStatus]}
        </p>
        <p className="type-subtext text-muted-foreground">{ASSURANCE_COPY[receipt.assurance]}</p>
        <Fields>
          <Field label="Receipt">
            <Mono>{receipt.receiptId}</Mono>
          </Field>
          <Field label="Issued">{formatDateTime(receipt.issuedAt)}</Field>
          <Field label="Verifier">{receipt.verifierActorId}</Field>
          <Field label="Signed by">
            <Mono>
              {receipt.signatureKeyId} · {receipt.signatureAlgorithm}
            </Mono>
          </Field>
          <Field label="Policy">
            <Mono>{receipt.policyRef}</Mono>
          </Field>
          <Field label="Contract">
            <Mono>
              {receipt.contractId}:{receipt.contractVersion}
            </Mono>
          </Field>
          <Field label="Supersedes">
            {receipt.supersedesReceiptId ? (
              <Mono>{receipt.supersedesReceiptId}</Mono>
            ) : (
              <span className="text-muted-foreground">{NO_PREDECESSOR}</span>
            )}
          </Field>
          <Field label="Replay">
            <span className="flex flex-wrap items-center gap-2">
              <ToneBadge
                tone={
                  receipt.replay.status === "REPLAYED"
                    ? "pass"
                    : receipt.replay.status === "REPLAYABLE"
                      ? "accent"
                      : "fail"
                }
              >
                {receipt.replay.status}
              </ToneBadge>
              <ReasonCodes codes={receipt.replay.reasonCodes} />
            </span>
          </Field>
          <Field label="Replay command">
            {receipt.replay.commands.length ? (
              <Mono>{receipt.replay.commands.join(" ; ")}</Mono>
            ) : (
              <span className="text-muted-foreground">No replay command recorded</span>
            )}
          </Field>
          <Field label="Independence">
            <ReasonCodes codes={receipt.independenceDisclosures} />
          </Field>
        </Fields>
      </CardContent>
    </Card>
  );
}

export function WorkChainView({ chain }: { chain: VerificationWorkChain }) {
  const latestReceipt = chain.receipts[0] ?? null;
  const fixture = chain.subject.class !== "real-subject";
  const awaitingOutput =
    !latestReceipt &&
    chain.stop.stage === "attempt" &&
    chain.attempts.some((attempt) => attempt.state === "OPEN");
  const passedCount = chain.obligations.filter(
    (obligation) => obligation.determination?.status === "PASS",
  ).length;

  return (
    <div className="flex flex-col gap-5" data-work-chain-stop-stage={chain.stop.stage}>
      <StatGrid cols={4}>
        <Stat
          label={awaitingOutput ? "Next stage" : "Stopped at"}
          value={awaitingOutput ? "Worker output" : STOP_STAGE_LABEL[chain.stop.stage]}
          tone={awaitingOutput ? "accent" : chain.stop.stage === "complete" ? "pass" : "fail"}
          foot={chain.stop.role ? `obligation ${chain.stop.role}` : "no obligation implicated"}
        />
        <Stat
          label="Aggregate verdict"
          value={latestReceipt ? latestReceipt.aggregateStatus : "No receipt"}
          tone={latestReceipt ? VERDICT_STAT_TONE[latestReceipt.aggregateStatus] : "unknown"}
          foot={
            latestReceipt
              ? latestReceipt.aggregateStatus === "PASS"
                ? "a verdict was reached"
                : "not a pass"
              : "nothing was ever decided"
          }
        />
        <Stat
          label="Assurance"
          value={latestReceipt ? latestReceipt.assurance : "None"}
          tone={latestReceipt?.assurance === "PROSPECTIVE" ? "accent" : "unknown"}
          foot={
            latestReceipt?.assurance === "PROSPECTIVE"
              ? "can carry completion authority"
              : "cannot authorise completion"
          }
        />
        <Stat
          label="Subject"
          value={fixture ? "Fixture" : "Real"}
          tone={fixture ? "unknown" : "accent"}
          foot={fixture ? "the system verifying itself" : "work outside the system's own fixtures"}
        />
      </StatGrid>

      <Callout
        tone={awaitingOutput ? "accent" : stopTone(chain.stop.stage)}
        title={
          awaitingOutput
            ? "Worker attempt open · awaiting output"
            : `You are here — ${STOP_STAGE_LABEL[chain.stop.stage]}: ${chain.stop.title}`
        }
        data-work-chain-stop="true"
      >
        <div className="flex flex-col gap-2">
          <span>
            {awaitingOutput
              ? "The worker has an open attempt. Ask the assigned worker to continue this attempt, then refresh after output is submitted. No verification result has been issued yet."
              : chain.stop.detail}
          </span>
          {chain.stop.obligationId ? (
            <span className="type-subtext">
              Obligation <Mono>{chain.stop.obligationId}</Mono>
            </span>
          ) : null}
          <ReasonCodes codes={chain.stop.reasonCodes} />
        </div>
      </Callout>

      {fixture ? (
        <Callout tone="unknown" title="This is not a customer's work">
          {chain.subject.label}. The subject recorded for this Work is{" "}
          <Mono>{chain.subject.sourceUri}</Mono>, classified because {chain.subject.basis}. A
          verdict here says the verification system exercised itself, not that any real work was
          checked.
        </Callout>
      ) : null}

      <Section
        title="Obligation chain"
        note="Declared by the accepted Contract, in the order it declares them. The first row that did not pass is where this Work stopped."
      >
        {chain.obligations.length ? (
          <DataTable
            rows={[...chain.obligations]}
            columns={obligationColumns(chain)}
            rowKey={(obligation) => obligation.obligationId}
            rowAttributes={(obligation) => ({
              "data-work-chain-obligation": obligation.role,
              "data-work-chain-outcome": obligation.outcome,
              "data-work-chain-stop-row":
                chain.stop.obligationId === obligation.obligationId ? "true" : undefined,
            })}
            emptyState={{
              title: "No obligations declared",
              description: "No accepted Contract declares an obligation for this Work.",
            }}
          />
        ) : (
          <Callout title="No obligations declared">
            No accepted Contract declares an obligation for this Work, so there was never anything
            to verify.
          </Callout>
        )}
      </Section>

      <Section
        title={
          chain.receipts.length > 1
            ? `Verdict receipts (${chain.receipts.length})`
            : "Verdict receipt"
        }
        note="A receipt is a record of what was decided — including a record that nothing was decided. The status and the assurance class are separate facts and are shown separately."
      >
        {chain.receipts.length ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {chain.receipts.map((receipt, index) => (
              <ReceiptCard key={receipt.receiptId} receipt={receipt} latest={index === 0} />
            ))}
          </div>
        ) : (
          <Callout tone="unknown" title="No receipt issued">
            No verdict Receipt exists for this Work. Nothing has been decided about it — which is
            not the same as it having failed.
          </Callout>
        )}
      </Section>

      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Subject" note="What this Work claims to be about.">
          <Card>
            <CardContent>
              <Fields>
                <Field label="Class">
                  <span className="flex flex-wrap items-center gap-2">
                    <ToneBadge tone={fixture ? "unknown" : "accent"}>
                      {chain.subject.label}
                    </ToneBadge>
                  </span>
                </Field>
                <Field label="Why">{chain.subject.basis}</Field>
                <Field label="Source">
                  <Mono>{chain.subject.sourceUri}</Mono>
                </Field>
                <Field label="Source kind">{chain.subject.sourceKind}</Field>
                <Field label="Source digest">
                  <Mono>{shortDigest(chain.subject.sourceDigest)}</Mono>
                </Field>
                <Field label="Work state">{chain.work.state}</Field>
                <Field label="Consequence">{chain.work.consequence}</Field>
                <Field label="Requested by">{chain.work.requestedByActorId}</Field>
                <Field label="Updated">{formatDateTime(chain.work.updatedAt)}</Field>
              </Fields>
            </CardContent>
          </Card>
        </Section>

        <Section
          title="Contract and attempts"
          note="The obligations above come from this Contract; the evidence comes from these Attempts."
        >
          <Card>
            <CardContent className="flex flex-col gap-3">
              {chain.contract ? (
                <Fields>
                  <Field label="Contract">
                    <Mono>
                      {chain.contract.contractId}:{chain.contract.version}
                    </Mono>
                  </Field>
                  <Field label="Digest">
                    <Mono>{shortDigest(chain.contract.contractDigest)}</Mono>
                  </Field>
                  <Field label="Deployment">{chain.contract.deploymentMode}</Field>
                  <Field label="Accepted">{formatDateTime(chain.contract.acceptedAt)}</Field>
                  <Field label="Accepted by">{chain.contract.acceptedByActorId}</Field>
                  <Field label="Obligations">
                    {chain.contract.obligationCount} declared · {passedCount} passed
                  </Field>
                </Fields>
              ) : (
                <Callout title="No contract">
                  No Contract was ever compiled and accepted for this Work.
                </Callout>
              )}
              {chain.attempts.length ? (
                chain.attempts.map((attempt) => (
                  <Fields key={attempt.attemptId}>
                    <Field label="Attempt">
                      <Mono>{attempt.attemptId}</Mono>
                    </Field>
                    <Field label="State">
                      <ToneBadge tone={attempt.state === "SUBMITTED" ? "pass" : "unknown"}>
                        {attempt.state}
                      </ToneBadge>
                    </Field>
                    <Field label="Worker">{attempt.workerActorId}</Field>
                    <Field label="Opened">{formatDateTime(attempt.openedAt)}</Field>
                    <Field label="Submitted">
                      {attempt.submittedAt ? (
                        formatDateTime(attempt.submittedAt)
                      ) : (
                        <span className="text-muted-foreground">Never submitted</span>
                      )}
                    </Field>
                    <Field label="Supersedes">
                      {attempt.supersedesAttemptId ? (
                        <Mono>{attempt.supersedesAttemptId}</Mono>
                      ) : (
                        <span className="text-muted-foreground">{NO_PREDECESSOR}</span>
                      )}
                    </Field>
                  </Fields>
                ))
              ) : (
                <Callout title="No attempts">No Attempt was ever opened for this Work.</Callout>
              )}
            </CardContent>
          </Card>
        </Section>
      </div>
    </div>
  );
}
